import React, { useEffect, useRef, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { AppWindow, BriefcaseBusiness, Building, Building2, ClipboardPenLine, Contact2, GraduationCap, LucideSchool, Map, School2, TabletSmartphone } from "lucide-react";
import { FaCheckCircle, FaExclamationCircle, FaSchool } from "react-icons/fa";
import SectionForm from "./SectionForm";
import ErrorFocusForm from "./ErrorFocusForm";
import ErrorFieldForm from "./ErrorFieldForm";
import axios from "axios";
import { getToken } from "../../../project/helpers/Jwt";
import PlatformBadge from "./PlatformBadge";
import LevelRegionSelector from "./LevelRegionSelector";
import RegionSelector from "./RegionSelector";
import InputGroupForm from "./InputGroupForm";
import InputImage from "../../../project/component/Input/InputImage";
import Signature from "./SignatureForm";
import PreviewItem from "./PreviewItem";
import PreviewImage from "./PreviewImage";
import Select, { components } from "react-select";


export default function WizardForm() {
    const navigate = useNavigate();

    const softwareSystems = [
        {
            value: "zone",
            title: "Citizen Super App",
            subtitle: "(one-stop solution)",
            description: "Kelola Data Warga, Surat Menyurat, Layanan Terpadu, Laporan dan Arsip"
        },
        {
            value: "academic", title: "Student Information System",
            subtitle: "(EdTech)",
            description: "Kelola Data Siswa, Guru, Pegawai, Jadwal, Nilai, Payroll, Absensi dan Arsip"
        },
        {
            value: "umroh.com", title: "Multi-Branch Control",
            subtitle: "(Travel Agent)",
            description: "Kelola Data Customer, Paket Perjalanan, Kuota, Booking, Payment, Supplier, Sistem Agensi dan Monitoring Progress"
        },
        {
            value: "ehajj", title: "B2G Seat Booking",
            subtitle: "(KBIH)",
            description: "Kelola Kuota, Distribusi, Porsi, Paket, Booking, Penjualan Masyair dan Penempatan"
        },
        {
            value: "pos", title: "Point of Sales",
            subtitle: "(UKM - Retails)",
            description: "Kelola Data Penjualan, Customer, Produk, Stok, Transaksi, Supplier, Distribusi dan Laporan"
        },
        {
            value: "core_cooperative_system", title: "Core Cooperative System",
            subtitle: "(e-Koperasi)",
            description: "Kelola Data Pegawai, Anggota, Nasabah, Sistem Tabungan, Penagihan, Keuangan, Aset, Transaksi dan Arsip"
        },
    ]

    const appMode = [
        {
            value: "Ready-made Apps",
            title: "Ready-made Apps",
            subtitle: "Siap Pakai",
            icon: "🚀",
            features: [
                "Implementasi Cepat",
                "Fitur Standar",
            ],
            color:
                "border-emerald-300 bg-emerald-50",
        },
        {
            value: "Custom Apps",
            title: "Custom Apps",
            subtitle: "Sesuai Kebutuhan",
            icon: "🛠️",
            features: [
                "Custom Branding",
                "Custom Workflow",
                "Integrasi Sistem",
            ],
            color:
                "border-amber-300 bg-amber-50",
        },
    ];

    const educationLevel = [
        {
            value: "pend-dasar",
            title: "Pendidikan Dasar",
            description: "SD / SMP - Mi / MTs - SR",
            platformName: "SIS",
            status: "CLOSED"
        },
        {
            value: "pend-menengah",
            title: "Pendidikan Menengah",
            description: "SMA / SMK - MA - SR",
            platformName: "SIS",
            status: "CLOSED"
        },
        {
            value: "pend-tinggi",
            title: "Pendidikan Tinggi",
            description: "Universitas / Institut Setaraf",
            platformName: "AIS",
            status: "CLOSED"
        },
    ];

    const educationInstitutionOptions = {
        "pend-dasar": {
            Kemendikdasmen: [
                "Sekolah Dasar (SD)",
                "Sekolah Menengah Pertama (SMP)",
            ],
            "Kementerian Agama": [
                "Madrasah Ibtidaiyah (MI)",
                "Madrasah Tsanawiyah (MTs)",
            ],
            "Kementerian Sosial": [
                "Sekolah Rakyat",
            ],
        },

        "pend-menengah": {
            Kemendikdasmen: [
                "Sekolah Menengah Atas (SMA)",
                "Sekolah Menengah Kejuruan (SMK)",
            ],
            "Kementerian Agama": [
                "Madrasah Aliyah (MA)",
                "Madrasah Aliyah Kejuruan (MAK)",
            ],
            "Kementerian Sosial": [
                "Sekolah Rakyat",
            ],
        },

        "pend-tinggi": {
            Kemendiktisaintek: [
                "Universitas",
                "Institut",
                "Sekolah Tinggi",
                "Politeknik",
                "Akademi",
                "Akademi Komunitas",
            ],
            "Kementerian Agama": [
                "UIN",
                "IAIN",
                "STAIN",
                "Sekolah Tinggi Agama Islam",
            ],
            "Kementerian Dalam Negeri": [
                "Institut Pemerintahan Dalam Negeri",
            ],
            "Kementerian Keuangan": [
                "Politeknik Keuangan Negara STAN",
            ],
            "Kementerian Imigrasi dan Pemasyarakatan": [
                "Politeknik Imigrasi",
            ]
        },
    };

    const [educationLevels, setEducationLevels] = useState(educationLevel);

    const regionLevel = [
        {
            value: "province",
            title: "Tingkat Provinsi"
        },
        {
            value: "city",
            title: "Tingkat Kabupaten/ Kota"
        },
        {
            value: "district",
            title: "Tingkat Kecamatan"
        },
    ];

    const scopesInstitutionLevel = [
        {
            value: "regional",
            title: "Regional (Level Dinas)",
            icon: <Building2 className="w-5 h-5 text-green-600" />,
            description:
                "Lengkapi data instansi yang membawahi sekolah atau satuan pendidikan.",
        },
        {
            value: "sectoral",
            title: "Sektoral (Level Sekolah)",
            icon: <BriefcaseBusiness className="w-5 h-5 text-green-600" />,
            description:
                "Lengkapi data sekolah atau satuan pendidikan.",
        },
    ];

    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const messageRef = useRef(null); // 🔹 Tambahkan ref
    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [resetImage, setResetImage] = useState(0);

    useEffect(() => {
        if (successMessage || errorMessage) {
            messageRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        }
    }, [successMessage, errorMessage]);

    const endpointProvinces = "api/zone/region/provinces";
    const [dataProvinces, setDataProvinces] = useState([]);
    const [selectedProvince, setProvince] = useState("");
    const [loadingProvinces, setLoadingProvinces] = useState(true);

    const endpointCities = "api/zone/region/cities";
    const [dataCities, setDataCities] = useState([]);
    const [selectedCity, setCity] = useState("");
    const [loadingCities, setLoadingCities] = useState(false);

    const endpointDistrict = "api/zone/region/districts";
    const [dataDistricts, setDataDistricts] = useState([]);
    const [selectedDistrict, setDistrict] = useState("");
    const [loadingDistricts, setLoadingDistricts] = useState(false);

    const [platformName, setPlatformName] = useState("");


    const fetchData = async (endpoint, { setData, setLoading }) => {
        try {
            setLoading(true);
            const startTime = Date.now();
            const response = await axios.get(endpoint);
            // const response = await axios.get(endpoint, {
            //     headers: {
            //         'Content-Type': 'application/json',
            //         'Authorization': `Bearer ${getToken()}`
            //     }
            // });
            setData(response.data || []);

            // Minimal tampil loading 800ms
            const elapsed = Date.now() - startTime;
            const remaining = Math.max(0, 800 - elapsed);

            await new Promise(resolve =>
                setTimeout(resolve, remaining)
            );
        } catch (err) {
            console.error('Error fetching price data:', err);
        } finally {
            setLoading(false);
        }
    };

    const [initialValues, setInitialValues] = useState({
        software: "",
        app_mode: "",
        education_level: "",
        institution_ministry: "",
        institution_type: "",
        level_region: "",
        total_staff: "",
        total_teacher: "",
        total_student: "",
        pic_name: "",
        pic_position: "",
        pic_contact: "",
        pic_password: "password",
        leader: null,
        corp_type: "",
        corp_name: "",
        institute_level: "region",
        institute_name: "",
        province: "",
        city: "",
        district: "",
        office_address: "",
        office_city: "",
        contact_official: "",
        email_official: "",
        web_official: "",
        platform_version: "hijrah.co.id",
        logo: null,
        stamp: null,
        signature: null,
    });

    const schemas = {
        1: Yup.object({
            software: Yup.string().required("Pilih Software System"),
            app_mode: Yup.string().required("Pilih mode aplikasi"),
            education_level: Yup.string()
                .when("software", {
                    is: (value) => ["academic"].includes(value),
                    then: (schema) =>
                        schema.required("Pilih Level Pendidikan"),
                    otherwise: (schema) => schema.notRequired(),
                }),
            institution_ministry: Yup.string().when(
                ["software", "education_level"],
                {
                    is: (software) => software == "academic",
                    then: (schema) => schema.required("Kementerian Pembina wajib dipilih"),
                    otherwise: (schema) => schema.notRequired()
                }
            ),
            institution_type: Yup.string().when(["software", "education_level"],
                {
                    is: (software) => software == "academic",
                    then: (schema) => schema.required("Jenis Pendidikan Tinggi wajib dipilih"),
                    otherwise: (schema) => schema.notRequired()
                }
            ),
            level_region: Yup.string().when(
                ["software", "app_mode"],
                {
                    is: (software, appMode) =>
                        software === "zone" || (software === "academic" && appMode === "Ready-made Apps"),
                    then: (schema) =>
                        schema.required("Pilih Level Wilayah"),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
            province: Yup.string()
                .when("level_region", {
                    is: (value) =>
                        ["province", "city", "district"].includes(value),
                    then: (schema) =>
                        schema.required("Provinsi wajib dipilih"),
                    otherwise: (schema) =>
                        schema.notRequired(),
                }),
            city: Yup.string()
                .when("level_region", {
                    is: (value) => ["city", "district"].includes(value),
                    then: (schema) => schema.required("Kabupaten/ Kota wajib dipilih"),
                    otherwise: (schema) => schema.notRequired()
                }),
            district: Yup.string()
                .when("level_region", {
                    is: (value) => ["district"].includes(value),
                    then: (schema) => schema.required("Kecamatan wajib dipilih"),
                    otherwise: (schema) => schema.notRequired()
                })
        }),
        2: Yup.object({
            institute_level: Yup.string().when(["software", "app_mode"],
                {
                    is: (software, appMode) =>
                        software === "academic" && appMode === "Ready-made Apps",
                    then: (schema) =>
                        schema.required("Pilih Lingkup Instansi"),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
            total_staff: Yup.number()
                .when(
                    ["software", "app_mode", "institute_level"],
                    {
                        is: (software, appMode, instituteLevel) =>
                            (software !== "zone" && software !== "academic") ||
                            (software === "academic" && instituteLevel == 'sectoral'),
                        then: (schema) =>
                            schema.required("Isi total staf")
                                .positive("Harus bernilai positif")
                                .typeError("Jumlah staff harus berupa angka.")
                                .integer("Jumlah staff harus berupa bilangan bulat.")
                                .min(1, "Jumlah staff minimal 1."),
                        otherwise: (schema) => schema.notRequired(),
                    }),
            total_teacher: Yup.number().when(
                ["software", "app_mode", "institute_level"],
                {
                    is: (software, appMode, instituteLevel) =>
                        (software === "academic" && instituteLevel == 'sectoral'),
                    then: (schema) =>
                        schema.required("Isi total guru/dosen")
                            .positive("Harus bernilai positif")
                            .typeError("Jumlah guru/dosen harus berupa angka.")
                            .integer("Jumlah guru/dosen harus berupa bilangan bulat.")
                            .min(1, "Jumlah guru/dosen minimal 1."),
                    otherwise: (schema) => schema.notRequired(),
                }),
            total_student: Yup.number().when(["software", "app_mode", "institute_level"],
                {
                    is: (software, appMode, instituteLevel) =>
                        (software === "academic" && instituteLevel == 'sectoral'),
                    then: (schema) =>
                        schema.required("Isi total siswa/mahasiswa")
                            .positive("Harus bernilai positif")
                            .typeError("Jumlah siswa/mahasiswa harus berupa angka.")
                            .integer("Jumlah siswa/mahasiswa harus berupa bilangan bulat.")
                            .min(1, "Jumlah siswa/mahasiswa minimal 1."),
                    otherwise: (schema) => schema.notRequired(),
                }),
            logo: Yup.mixed().required("Logo wajib diisi"),
            stamp: Yup.mixed().required("Cap/stempel wajib diisi"),
            corp_type: Yup.string().when(
                ["software", "app_mode"],
                {
                    is: (software, appMode) =>
                        (software !== "zone" && software !== "academic") ||
                        (software === "academic" && appMode === "Custom Apps"),
                    then: (schema) =>
                        schema.required("Badan hukum wajib dipilih"),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
            corp_name: Yup.string().when(["software", "app_mode"], ([software, appMode], schema) => {
                return schema.required(
                    (software !== "zone" && software !== "academic") ||
                        (software === "academic" && appMode === "Custom Apps")
                        ? "Nama Instansi/Unit Kerja wajib diisi"
                        : "Nama Instansi wajib diisi"
                );
            }),
            institute_name: Yup.string().when(
                ["software", "app_mode"],
                {
                    is: (software, appMode) =>
                        (software !== "zone" && software !== "academic") ||
                        (software === "academic" && appMode === "Custom Apps"),
                    then: (schema) =>
                        schema.required("Nama Brand/ Hak Paten wajib diisi"),
                    otherwise: (schema) => schema.notRequired(),
                }
            ),
            leader: Yup.string().required("Nama Pimpinan wajib diisi"),
            office_address: Yup.string().required("Alamat Kantor wajib diisi"),
            office_city: Yup.string().required("Kota/ Kabupaten Kantor wajib diisi"),
            contact_official: Yup.string().required("Kontak Resmi wajib diisi"),
            email_official: Yup.string().required("Email Resmi wajib diisi").trim()
                .max(100, "Email maksimal 100 karakter.")
                .email("Format email tidak valid."),
            // web_official: Yup.string().url("Format URL tidak valid"),
        }),
        3: Yup.object({
            pic_name: Yup.string().required("Nama PIC wajib diisi"),
            pic_contact: Yup.string().required("Contact PIC wajib diisi"),
            pic_position: Yup.string().required("Jabatan PIC wajib diisi"),
            signature: Yup.mixed().required("Tanda tangan wajib diisi"),
        }),
        4: Yup.object({
            agreement: Yup.boolean()
                .oneOf([true], "Saya menyetujui pernyataan ini.")
        })
    };

    useEffect(() => {
        if (!selectedProvince) return;

        setLoadingCities(true);

        fetchData(
            `${BASE_URL}${endpointCities}?province=${encodeURIComponent(selectedProvince)}`,
            {
                setData: setDataCities,
                setLoading: setLoadingCities,
            }
        );
    }, [selectedProvince]);

    useEffect(() => {
        if (!selectedCity) return;

        setLoadingDistricts(true);

        fetchData(
            `${BASE_URL}${endpointDistrict}?city=${encodeURIComponent(selectedCity)}`,
            {
                setData: setDataDistricts,
                setLoading: setLoadingDistricts,
            }
        );
    }, [selectedCity]);

    const [step, setStep] = useState(1);

    const next = async (validateForm, setTouched) => {
        console.log("NEXT");
        const errors = await validateForm();

        if (Object.keys(errors).length > 0) {
            setTouched(
                Object.keys(errors).reduce((acc, key) => {
                    acc[key] = true;
                    return acc;
                }, {})
            );
            return;
        }

        setStep((prev) => prev + 1);
    };

    const previous = () => setStep((s) => s - 1);

    const onSubmit = async (values, { setSubmitting, resetForm }) => {
        setErrorMessage(null);
        setSuccessMessage(null);

        try {
            const formData = new FormData();
            Object.entries(values).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const response = await axios.post(
                `${BASE_URL}api/superapps/project/requestapp`,
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                }
            );

            if (response.status === 200 || response.status === 201) {
                setSuccessMessage("Form berhasil dikirim!");
                resetForm();
                setResetImage(prev => prev + 1);

                setStep(1)
            } else {
                setErrorMessage(
                    response.data?.message || "Gagal mengirim form, coba lagi."
                );
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setErrorMessage(
                error.response?.data?.message || "Terjadi kesalahan saat mengirim form."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const Option = (props) => (
        <components.Option {...props}>
            <div className="py-1">
                <div className="font-semibold">
                    {props.data.title}
                </div>
                <div className="text-xs text-gray-500">
                    {props.data.subtitle}
                </div>
            </div>
        </components.Option>
    );

    const SingleValue = (props) => (
        <components.SingleValue {...props}>
            <div>
                <div className="font-medium">
                    {props.data.title}
                </div>
                <div className="text-xs text-gray-500">
                    {props.data.subtitle}
                </div>
            </div>
        </components.SingleValue>
    );

    return (
        <div className="pt-20 pb-20 px-3 sm:px-6 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-xl p-6 sm:p-8 border border-gray-100">
                <span className="font-semibold mb-3 flex items-center justify-center gap-3 text-[#f29f02]">
                    <ClipboardPenLine className="w-8 h-8" />
                    <span className="text-2xl">
                        Form Request Apps
                    </span>
                </span>

                <div ref={messageRef}>
                    {successMessage && (
                        <div className="mb-4 bg-green-50 border border-green-400 text-green-700 p-3 rounded-md text-sm flex items-center gap-2">
                            <FaCheckCircle />
                            <span>{successMessage}</span>
                        </div>
                    )}
                    {errorMessage && (
                        <div className="mb-4 bg-red-50 border border-red-300 text-red-700 p-3 rounded-md text-sm flex items-start gap-2">
                            <FaExclamationCircle className="mt-0.5" />
                            <span>{errorMessage}</span>
                        </div>
                    )}
                </div>

                <Formik
                    initialValues={initialValues}
                    validationSchema={schemas[step]}
                    onSubmit={(values, helpers) => {
                        if (step < 4) {
                            setStep(4);
                            helpers.setSubmitting(false);
                            return;
                        }

                        onSubmit(values, helpers);
                    }}
                    enableReinitialize
                >
                    {({ validateForm, setTouched, setFieldValue, values, isSubmitting }) => {
                        const selectedSoftware = softwareSystems.find(
                            item => item.value === values.software
                        );

                        var isAcademicApp = values.software == "academic";
                        var isZoneApp = values.software == "zone";
                        var isReadyMadeApp = values.app_mode === "Ready-made Apps";


                        var isAcademicReadyMadeApp = isAcademicApp && isReadyMadeApp;
                        var isAcademicCustomApp = isAcademicApp && !isReadyMadeApp;
                        var isAcademicHigh = isAcademicApp && values.education_level === "pend-tinggi";

                        var isAcademicLevelUnit = isAcademicCustomApp ||
                            (isAcademicReadyMadeApp && values.institute_level == 'sectoral');

                        var isZoneReadyMadeApp = isZoneApp && isReadyMadeApp;
                        var isZoneCustomApp = isZoneApp && !isReadyMadeApp;

                        var isNeedRegionData = isZoneApp || isAcademicReadyMadeApp;
                        var isNeedStaff = !isZoneApp && !isAcademicApp || (isAcademicApp && values.institute_level== 'sectoral');

                        const institutionField = [
                            {
                                type: "select", label: "Badan Hukum/ Usaha", name: "corp_type",
                                hidden: (values) => isZoneApp || isAcademicReadyMadeApp,
                                options: [
                                    { value: "", label: "Pilih Badan Hukum/ Usaha" },
                                    { value: "Yayasan", label: "Yayasan" },
                                    { value: "Perkumpulan", label: "Perkumpulan" },
                                    { value: "Badan Wakaf", label: "Badan Wakaf" },
                                    { value: "Badan Hukum Publik", label: "Badan Hukum Publik" },
                                    { value: "PTN-BH", label: "PTN-BH" },
                                    { disabled: true, value: null, label: "──────────" },
                                    { value: "Koperasi/ BMT", label: "Koperasi/ BMT" },
                                    { value: "Perseroan Terbatas (PT)", label: "Perseroan Terbatas (PT)" },
                                    { value: "BUMN/BUMD", label: "BUMN/BUMD" },
                                    { disabled: true, value: null, label: "──────────" },
                                    { value: "Unit Dagang (UD)", label: "Unit Dagang (UD)" },
                                    { value: "Commanditaire Vennootschap (CV)", label: "Commanditaire Vennootschap (CV)" },
                                    { value: "Firma (Fa)", label: "Firma (Fa)" },
                                ]
                            },
                            {
                                type: "text", label: "Nama Instansi/Unit Kerja Terdaftar", name: "corp_name",
                                hidden: (values) => isZoneApp,
                            },
                            {
                                type: "text", label: "Nama Brand/ Hak Paten", name: "institute_name",
                                hidden: (values) => isZoneApp || isAcademicReadyMadeApp,
                            },
                            {
                                type: "text", label: "Nama Instansi Pemerintahan", name: "corp_name",
                                disabled: true, hint: "Autofill Instansi Pemerintahan",
                                hidden: (values) => !isZoneApp,
                            },
                            { type: "text", label: "Nama Pimpinan", name: "leader" },
                            { type: "text", label: "Alamat Kantor", name: "office_address", as: "textarea" },
                            { type: "text", label: "Kabupaten/ Kota", name: "office_city", hint: "Jakarta Selatan" },
                            { type: "text", label: "Telp. Kantor", name: "contact_official" }, //phone
                            { type: "email", label: "Email Resmi", name: "email_official", hint: "user@email.com" },
                            { type: "url", label: "Website Resmi", name: "web_official", hint: "https://domain.com" },
                        ];

                        const levelRegionItems = isAcademicReadyMadeApp
                            ? [
                                {
                                    value: "province",
                                    title: "Provinsi",
                                    status:
                                        values.education_level === "pend-menengah"
                                            ? "OPEN"
                                            : "CLOSED",
                                },
                                {
                                    value: "city",
                                    title: "Kabupaten / Kota",
                                    status:
                                        values.education_level === "pend-dasar"
                                            ? "OPEN"
                                            : "CLOSED",
                                },
                            ]
                            : regionLevel;

                        const levelRegionTitle = isAcademicReadyMadeApp
                            ? "Level Wilayah"
                            : "Level Instansi Pemerintahan";

                        const displayLevelRegion = () => {
                            if (values.level_region == "province") return "Provinsi";
                            if (values.level_region == "district") return "Kecamatan";
                            if (values.level_region == "city") return "Kota/ Kabupaten";
                            return values.level_region;
                        }

                        const displayLevelEducation = () => {
                            if (values.education_level == "pend-dasar") return "Pendidikan Dasar";
                            if (values.education_level == "pend-menengah") return "Pendidikan Menengah";
                            if (values.education_level == "pend-tinggi") return "Pendidikan Tinggi";
                            return values.education_level;
                        }

                        const displaySoftware = () => {
                            if (values.software === "academic") {
                                if (values.education_level === "pend-tinggi") {
                                    return "Academic Information System (AIS)";
                                }

                                return "Student Information System (SIS)";
                            }

                            return (
                                softwareSystems.find(
                                    (item) => item.value === values.software
                                )?.title ?? values.software
                            );
                        };

                        return <Form className="space-y-5 mt-5">
                            <ErrorFocusForm />
                            <div className="flex gap-2">
                                {[1, 2, 3, 4].map((item) => (
                                    <div
                                        key={item}
                                        className={`h-2 flex-1 rounded ${step >= item ? "bg-[#f6a208]" : "bg-gray-200"
                                            }`}
                                    />
                                ))}
                            </div>

                            {step === 1 && (
                                <>
                                    <SectionForm
                                        key="section_software"
                                        icon={<AppWindow className="w-5 h-5" />} title="Software System" color="blue">
                                        <Field
                                            key="software"
                                            name="software">
                                            {({ field, form }) => (
                                                <Select
                                                    isSearchable={false}
                                                    options={softwareSystems}
                                                    getOptionValue={(option) => option.value}
                                                    getOptionLabel={(option) => option.title}
                                                    components={{
                                                        Option,
                                                        SingleValue
                                                    }}
                                                    onChange={(selected) => {

                                                        const e = {
                                                            target: {
                                                                name: field.name,
                                                                value: selected?.value ?? "",
                                                            },
                                                        };

                                                        field.onChange(e);

                                                        setDataProvinces([]);
                                                        setDataCities([]);
                                                        setDataDistricts([]);

                                                        form.setFieldValue("level_region", "");
                                                        form.setFieldValue("province", "");
                                                        form.setFieldValue("city", "");
                                                        form.setFieldValue("district", "");

                                                        fetchData(`${BASE_URL}${endpointProvinces}`, {
                                                            setData: setDataProvinces,
                                                            setLoading: setLoadingProvinces
                                                        });

                                                        const softwareValue = selected?.value;


                                                        setPlatformName("");

                                                        if (softwareValue === "zone") {
                                                            setPlatformName("Zone");
                                                        } else if (softwareValue === "umroh.com") {
                                                            setPlatformName("Umroh.com");
                                                        } else if (softwareValue === "ehajj") {
                                                            setPlatformName("E-Hajj");
                                                        } else if (
                                                            softwareValue === "pos" ||
                                                            softwareValue === "core_cooperative_system"
                                                        ) {
                                                            setPlatformName("L`Bayt");
                                                        }
                                                    }}
                                                />
                                            )}
                                        </Field>
                                        <ErrorFieldForm name="software" />

                                        {selectedSoftware && (
                                            <div className="mt-3 rounded-md bg-blue-50 p-3">
                                                <h4 className="font-medium text-blue-700">
                                                    {selectedSoftware.title}
                                                </h4>
                                                <p className="text-sm text-gray-600">
                                                    {selectedSoftware.description}
                                                </p>
                                            </div>
                                        )}
                                    </SectionForm>
                                    <SectionForm
                                        key="section_app_mode"
                                        data-field="app_mode"
                                        icon={<TabletSmartphone className="w-5 h-5" />}
                                        title="Mode Aplikasi"
                                        color="blue">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            {appMode.map((item) => {
                                                const isReadyMadeApp = item.value === "Ready-made Apps";
                                                const selected =
                                                    values.app_mode === item.value;

                                                return (
                                                    <div
                                                        key={item.value}
                                                        onClick={() => {
                                                            setFieldValue("level_region", "");
                                                            setFieldValue("education_level", "");
                                                            setFieldValue("app_mode", item.value);
                                                            setEducationLevels(
                                                                educationLevel.map((level) => ({
                                                                    ...level,
                                                                    status:
                                                                        item.value === "Ready-made Apps" &&
                                                                            level.value === "pend-tinggi"
                                                                            ? "CLOSED"
                                                                            : "OPEN",
                                                                }))
                                                            );
                                                        }}
                                                        className={`
                                                        cursor-pointer rounded-xl border p-4 transition-all
                                                        ${selected
                                                                ? `${item.color} ring-2 ring-offset-1 ${isReadyMadeApp
                                                                    ? "ring-emerald-300"
                                                                    : "ring-amber-300"
                                                                }`
                                                                : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
                                                            }
                                                    `}
                                                    >
                                                        <div className="flex justify-between items-start">
                                                            <div>
                                                                <div className="text-2xl">
                                                                    {item.icon} {(platformName && isReadyMadeApp) && (
                                                                        <PlatformBadge name={platformName} />
                                                                    )}
                                                                </div>

                                                                <h3 className="font-semibold mt-2">
                                                                    {item.title}
                                                                </h3>

                                                                <p className="text-xs text-gray-500">
                                                                    {item.subtitle}
                                                                </p>
                                                            </div>

                                                            {selected && (
                                                                <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center">
                                                                    ✓
                                                                </div>

                                                            )}
                                                        </div>

                                                        <ul className="mt-3 space-y-1 text-xs text-gray-600">
                                                            {item.features.map((feature) => (
                                                                <li key={feature}>
                                                                    ✓ {feature}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                );
                                            })}
                                        </div>

                                        <ErrorFieldForm name="app_mode" />
                                    </SectionForm>

                                    {isAcademicApp && (
                                        <SectionForm
                                            key="section_education_level"
                                            data-field="education_level"
                                            icon={<LucideSchool className="w-5 h-5" />}
                                            title="Level Pendidikan"
                                            color="blue">
                                            <p className="rounded-md text-sm text-yellow-600 border-yellow-600 bg-yellow-50 ring-2 ring-yellow-200 p-2 my-3">
                                                *Jika ada lebih dari satu institusi/jenjang, silakan ajukan <span className="font-bold italic" >satu per satu</span> sesuai jumlahnya
                                            </p>
                                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                                {educationLevels.map((item) => {
                                                    const selected =
                                                        values.education_level === item.value;

                                                    const disabled = item.status === "CLOSED";

                                                    return (
                                                        <div
                                                            key={item.value}
                                                            onClick={() => {
                                                                if (disabled) return;

                                                                setFieldValue("education_level", item.value);
                                                                setFieldValue("level_region", "");
                                                                setFieldValue("province", "");
                                                                setFieldValue("city", "");
                                                                setFieldValue("district", "");

                                                                setProvince("");
                                                                setDataCities([]);
                                                                setDataDistricts([]);
                                                            }}
                                                            className={`rounded-xl border p-4 transition-all relative
                                                                ${disabled ? "border-gray-200 bg-gray-50 opacity-70 cursor-not-allowed"
                                                                    : selected ? "cursor-pointer border-blue-500 bg-blue-50 ring-2 ring-blue-200"
                                                                        : "cursor-pointer border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                                                                }
                `}
                                                        >
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div className="flex-1">
                                                                    <div className="flex flex-wrap items-center gap-2">
                                                                        <h3 className="font-semibold text-sm text-gray-900">
                                                                            {item.title}
                                                                        </h3>
                                                                        <PlatformBadge name={item.platformName} />
                                                                        <span
                                                                            className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${disabled
                                                                                ? "bg-red-100 text-red-700"
                                                                                : "bg-green-100 text-green-700"}`}
                                                                        >
                                                                            {disabled ? "Closed" : "Open"}
                                                                        </span>
                                                                    </div>

                                                                    <p className="text-xs text-gray-500 mt-2">
                                                                        {item.description}
                                                                    </p>
                                                                </div>

                                                                {selected && !disabled && (
                                                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-500 text-white text-xs shrink-0">
                                                                        ✓
                                                                    </div>
                                                                )}

                                                                {disabled && (
                                                                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-500 shrink-0">
                                                                        🔒
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <ErrorFieldForm name="education_level" />
                                        </SectionForm>
                                    )}

                                    {isAcademicApp && (
                                        <SectionForm
                                            key="section_institution_type"
                                            data-field="institution_type"
                                            icon={<School2 className="w-5 h-5" />}
                                            title="Jenis Satuan Pendidikan"
                                            color="blue"
                                        >
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                                {/* Kementerian Pembina */}
                                                <InputGroupForm
                                                    label="Kementerian Pembina"
                                                    data-field="institution_ministry"
                                                >
                                                    <Field
                                                        as="select"
                                                        name="institution_ministry"
                                                        className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]"
                                                        onChange={(e) => {
                                                            const value = e.target.value;

                                                            setFieldValue("institution_ministry", value);
                                                            setFieldValue("institution_type", "");
                                                        }}
                                                    >
                                                        <option value="">Pilih Kementerian</option>

                                                        {Object.keys(
                                                            educationInstitutionOptions[
                                                            values.education_level
                                                            ] || {}
                                                        ).map((item) => (
                                                            <option key={item} value={item}>
                                                                {item}
                                                            </option>
                                                        ))}
                                                    </Field>

                                                    <ErrorFieldForm name="institution_ministry" />
                                                </InputGroupForm>

                                                {/* Jenis Institusi */}
                                                <InputGroupForm
                                                    label="Jenis Pendidikan"
                                                    data-field="institution_type"
                                                >
                                                    <Field
                                                        as="select"
                                                        name="institution_type"
                                                        disabled={!values.institution_ministry}
                                                        className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]"
                                                    >
                                                        <option value="">
                                                            Pilih Jenis Pendidikan
                                                        </option>

                                                        {(
                                                            educationInstitutionOptions[
                                                            values.education_level
                                                            ]?.[
                                                            values.institution_ministry
                                                            ] || []
                                                        ).map((item) => (
                                                            <option key={item} value={item}>
                                                                {item}
                                                            </option>
                                                        ))}
                                                    </Field>

                                                    <ErrorFieldForm name="institution_type" />
                                                </InputGroupForm>
                                            </div>
                                        </SectionForm>
                                    )}

                                    {(isAcademicReadyMadeApp || isZoneApp) &&
                                        levelRegionItems.length > 0 && (
                                            <LevelRegionSelector
                                                title={levelRegionTitle}
                                                items={levelRegionItems}
                                                value={values.level_region}
                                                onChange={(v) => {
                                                    setFieldValue("level_region", v);

                                                    if (v !== values.level_region) {
                                                        setProvince("");
                                                        setDataCities([]);
                                                        setDataDistricts([]);

                                                        setFieldValue("province", "");
                                                        setFieldValue("city", "");
                                                        setFieldValue("district", "");

                                                        fetchData(
                                                            `${BASE_URL}${endpointProvinces}`,
                                                            {
                                                                setData: setDataProvinces,
                                                                setLoading:
                                                                    setLoadingProvinces,
                                                            }
                                                        );
                                                    }
                                                }}
                                                dataField={
                                                    isZoneApp ? "level_region" : undefined
                                                }
                                                error={
                                                    <ErrorFieldForm name="level_region" />
                                                }
                                            />
                                        )}

                                    {isNeedRegionData && (
                                        <RegionSelector
                                            loadingProvinces={loadingProvinces}
                                            loadingCities={loadingCities}
                                            loadingDistricts={loadingDistricts}
                                            dataProvinces={dataProvinces}
                                            dataCities={dataCities}
                                            dataDistricts={dataDistricts}
                                            setProvince={setProvince}
                                            setCity={setCity}
                                            setDistrict={setDistrict}
                                            setDataCities={setDataCities}
                                            setDataDistricts={setDataDistricts} />
                                    )}
                                </>
                            )}

                            {step === 2 && (
                                <>
                                    {isAcademicReadyMadeApp && (
                                        <SectionForm
                                            key="section_institute_level"
                                            data-field="institute_level"
                                            icon={<Map className="w-5 h-5" />}
                                            title="Lingkup Instansi"
                                            color="green">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {scopesInstitutionLevel.map((item) => {

                                                    isAcademicLevelUnit = values.institute_level === item.value;

                                                    return (
                                                        <div
                                                            key={item.value}
                                                            onClick={() => {
                                                                setFieldValue("institute_level", item.value);

                                                                // reset data yang bergantung pada pilihan
                                                                setFieldValue("institute_name", "");
                                                                setFieldValue("corp_name", "");

                                                            }}
                                                            className={`rounded-xl border p-4 transition-all cursor-pointer mt-3
                                                                ${isAcademicLevelUnit
                                                                    ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                                                                    : "border-gray-200 hover:border-green-300 hover:bg-gray-50"
                                                                }`}
                                                        >
                                                            <div className="flex items-start gap-3">
                                                                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                                                                    {item.icon}
                                                                </div>

                                                                <div className="flex-1">
                                                                    <h3 className="font-semibold text-sm">
                                                                        {item.title}
                                                                    </h3>

                                                                    <p className="text-xs text-gray-500 mt-1">
                                                                        {item.description}
                                                                    </p>
                                                                </div>

                                                                {isAcademicLevelUnit && (
                                                                    <div className="flex items-center justify-center w-6 h-6 rounded-full bg-green-500 text-white text-xs">
                                                                        ✓
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>

                                            <ErrorFieldForm name="institute_level" />
                                        </SectionForm>
                                    )}

                                    {isAcademicLevelUnit && (
                                        <SectionForm
                                            icon={<GraduationCap className="w-5 h-5" />}
                                            title="Pengaturan Akademik"
                                            color="blue"
                                        >
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                                {[
                                                    { label: "Guru/Dosen", name: "total_teacher", hint: "Est. Jumlah Guru/Dosen" },
                                                    { label: "Siswa/Mahasiswa", name: "total_student", hint: "Est. Jumlah Siswa/Mahasiswa" },
                                                ].map(({ label, name, hint }) => (
                                                    <InputGroupForm key={name} label={label}>
                                                        <Field
                                                            type="text"
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            name={name}
                                                            placeholder={hint}
                                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-gray-300"
                                                        />
                                                        <ErrorFieldForm name={name} />
                                                    </InputGroupForm>
                                                ))}
                                            </div>
                                        </SectionForm>
                                    )}

                                    {isNeedStaff && (
                                        <SectionForm
                                            icon={<Building className="w-5 h-5" />}
                                            title="Pengaturan Kepegawaian"
                                            color="blue">
                                            <div className="grid grid-cols-1 sm:grid-cols-1 gap-3">
                                                {[
                                                    { label: "Staf/Pegawai", name: "total_staff", hint: "Est. Jumlah Staf/Pegawai" },
                                                ].map(({ label, name, hint }) => (
                                                    <InputGroupForm key={name} label={label}>
                                                        <Field
                                                            type="text"
                                                            inputMode="numeric"
                                                            pattern="[0-9]*"
                                                            name={name}
                                                            placeholder={hint}
                                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-gray-300"
                                                        />
                                                        <ErrorFieldForm name={name} />
                                                    </InputGroupForm>
                                                ))}
                                            </div>
                                        </SectionForm>
                                    )}

                                    <SectionForm
                                        icon={<Building2 className="w-5 h-5" />}
                                        title="Data Instansi/Unit Kerja"
                                        color="blue">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                            <div
                                                className="flex flex-col items-center border-2 border-dashed border-yellow-300 rounded-lg p-3 bg-white hover:bg-yellow-50 transition">
                                                <InputImage
                                                    resetTrigger={resetImage}
                                                    value={values.logo}
                                                    attr={{
                                                        name: "logo",
                                                        placeholder: "Logo Instansi/Unit Kerja",
                                                        hint: "PNG (Tanpa Background)",
                                                        height: 180,
                                                        width: 180,
                                                    }}
                                                    handleCroppedFoto={(v) => {
                                                        setFieldValue("logo", v);
                                                    }}
                                                />
                                                <InputImage
                                                    data-field="stamp"
                                                    resetTrigger={resetImage}
                                                    value={values.stamp}
                                                    attr={{
                                                        name: "stamp",
                                                        placeholder: "Stempel Instansi/Unit Kerja",
                                                        hint: "PNG (Tanpa Background)",
                                                        height: 180,
                                                        width: 180,
                                                    }}
                                                    handleCroppedFoto={(v) => {
                                                        setFieldValue("stamp", v);
                                                    }}
                                                />
                                            </div>
                                            <div className="space-y-3">
                                                {institutionField
                                                    .filter(field => !field.hidden?.(values))
                                                    .map(({ label, name, hint, as, type, options, disabled }) => (
                                                        type == "select" ?
                                                            <InputGroupForm key={name} label={label} data-field={name}>
                                                                <Field
                                                                    as="select"
                                                                    name={name}
                                                                    className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]"
                                                                    onChange={(e) => {
                                                                        const { name, value } = e.target;
                                                                        setFieldValue(name, value);

                                                                        if (name === "high_education_ministry") {
                                                                            setFieldValue("high_education_type", "");
                                                                        }
                                                                    }}>
                                                                    {options.map((opt) => (
                                                                        <option disabled={opt.disabled ?? false} key={opt.value} value={opt.value}>
                                                                            {opt.label}
                                                                        </option>
                                                                    ))}
                                                                </Field>
                                                                <ErrorFieldForm name={name} />
                                                            </InputGroupForm> :
                                                            <InputGroupForm key={name} label={label} data-field={name}>
                                                                <Field
                                                                    type={type || "text"}
                                                                    name={name}
                                                                    disabled={disabled ?? false}
                                                                    placeholder={hint?.trim() || `Input ${label}`}
                                                                    className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-yellow-300"
                                                                />
                                                                <ErrorFieldForm name={name} />
                                                            </InputGroupForm>
                                                    ))}
                                            </div>
                                        </div>
                                    </SectionForm>
                                </>
                            )}

                            {step === 3 && (
                                <>
                                    <SectionForm
                                        icon={<Contact2 className="w-5 h-5" />}
                                        title="PIC Teknis"
                                        color="blue">
                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                            {[
                                                {
                                                    type: 'select',
                                                    label: 'Jabatan',
                                                    name: 'pic_position',
                                                    options: [
                                                        { value: '', label: 'Pilih PIC Teknis' },
                                                        { value: 'HelpDesk/ IT Support', label: 'HelpDesk/ IT Support' },
                                                        { value: 'Humas', label: 'Humas' },
                                                        { value: 'Keuangan/ Bendahara', label: 'Keuangan/ Bendahara' },
                                                        ...(isZoneApp || isAcademicApp
                                                            ? [
                                                                { value: 'Pranata Komputer', label: 'Pranata Komputer' },
                                                                { value: 'Tata Usaha', label: 'Tata Usaha' },
                                                            ]
                                                            : []),
                                                    ],
                                                },
                                                { type: 'text', label: "Nama", name: "pic_name" },

                                                { type: 'text', label: "Kontak", name: "pic_contact" }
                                            ].filter(field => !field.hidden?.(values))
                                                .map(({ label, name, type, options }) => (
                                                    type == "select" ? <InputGroupForm key={name} label={label} data-field={name}>
                                                        <Field
                                                            as="select"
                                                            name={name}
                                                            className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-[#f29f02]">

                                                            {options.map((opt) => (
                                                                <option disabled={opt.disabled ?? false} key={opt.value} value={opt.value}>
                                                                    {opt.label}
                                                                </option>
                                                            ))}
                                                        </Field>
                                                        <ErrorFieldForm name={name} />
                                                    </InputGroupForm> :
                                                        <InputGroupForm key={name} label={label}>
                                                            <Field
                                                                type={type || "text"}
                                                                name={name}
                                                                className="w-full p-2 border rounded-md text-sm focus:ring-2 focus:ring-green-300"
                                                            />
                                                            <ErrorFieldForm name={name} />
                                                        </InputGroupForm>
                                                ))}
                                        </div>
                                    </SectionForm>

                                    <Signature
                                        signedName1="Tanda Tangan Pemohon"
                                        onChangeResult={(sig) => setFieldValue("signature", sig)}
                                    />
                                    <ErrorFieldForm name="signature" />
                                </>
                            )}

                            {step === 4 && (
                                <div className="space-y-6">

                                    <SectionForm
                                        title="Informasi Sistem"
                                        color="blue"
                                    >
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <PreviewItem label="Software" value={displaySoftware()} />
                                            <PreviewItem label="Mode Aplikasi" value={values.app_mode} />
                                            <PreviewItem label="Jenjang Pendidikan" value={displayLevelEducation()} isEmptyShow={false} />
                                            <PreviewItem label="Kementrian Pembina" value={values.institution_ministry} isEmptyShow={false} />
                                            <PreviewItem label="Jenis Pendidikan" value={values.institution_type} isEmptyShow={false} />
                                            <PreviewItem label="Level Wilayah" value={displayLevelRegion()} isEmptyShow={false} />
                                            <PreviewItem label="Provinsi" value={values.province} isEmptyShow={false} />
                                            <PreviewItem label="Kabupaten/Kota" value={values.city} isEmptyShow={false} />
                                            <PreviewItem label="Kecamatan" value={values.district} isEmptyShow={false} />
                                        </div>
                                    </SectionForm>

                                    <SectionForm
                                        title="Informasi Instansi"
                                        color="green"
                                    >
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <PreviewItem label="Pimpinan" value={values.leader} />
                                            <PreviewItem label="Jenis Instansi" value={values.corp_type} isEmptyShow={false} />
                                            <PreviewItem label="Nama Instansi" value={values.corp_name} />
                                            <PreviewItem label="Nama Lembaga" value={values.institute_name} isEmptyShow={false} />
                                            <PreviewItem label="Alamat" value={values.office_address} />
                                            <PreviewItem label="Kota" value={values.office_city} />
                                            <PreviewItem label="Kontak Resmi" value={values.contact_official} />
                                            <PreviewItem label="Email" value={values.email_official} />
                                            <PreviewItem label="Website" value={values.web_official} />
                                            <PreviewItem label="Jumlah Staff" value={values.total_staff} isEmptyShow={false} />
                                            <PreviewItem label="Jumlah Guru" value={values.total_teacher} isEmptyShow={false} />
                                            <PreviewItem label="Jumlah Siswa" value={values.total_student} isEmptyShow={false} />
                                        </div>

                                        <div className="grid md:grid-cols-2 gap-6 mt-6">
                                            <PreviewImage label="Logo" src={values.logo} />
                                            <PreviewImage label="Stempel" src={values.stamp} />
                                        </div>
                                    </SectionForm>

                                    <SectionForm
                                        title="Penanggung Jawab"
                                        color="orange"
                                    >
                                        <div className="grid md:grid-cols-2 gap-4">
                                            <PreviewItem label="Nama PIC" value={values.pic_name} />
                                            <PreviewItem label="Jabatan" value={values.pic_position} />
                                            <PreviewItem label="Kontak" value={values.pic_contact} />
                                            <PreviewItem label="Password" value="••••••••" />
                                        </div>

                                        <div className="mt-6">
                                            <PreviewImage
                                                label="Tanda Tangan"
                                                src={values.signature}
                                            />
                                        </div>
                                    </SectionForm>


                                    <div className="mt-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                                        <div className="flex items-start gap-4">
                                            <Field name="agreement">
                                                {({ field, form }) => (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            form.setFieldValue(
                                                                "agreement",
                                                                !form.values.agreement
                                                            )
                                                        }
                                                        className={`
            mt-1 flex h-6 w-6 items-center justify-center
            rounded-md border-2 transition
            ${form.values.agreement
                                                                ? "border-green-500 bg-green-500 text-white"
                                                                : "border-gray-300 bg-white"
                                                            }
          `}
                                                    >
                                                        {form.values.agreement && "✓"}
                                                    </button>
                                                )}
                                            </Field>

                                            <div className="flex-1">
                                                <h4 className="font-semibold text-gray-800">
                                                    Pernyataan Persetujuan
                                                </h4>

                                                <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                                                    Saya menyatakan bahwa seluruh data yang diisi pada formulir ini
                                                    adalah benar, lengkap, dan dapat dipertanggungjawabkan.
                                                </p>
                                            </div>
                                        </div>

                                        <ErrorFieldForm name="agreement" />
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-between">
                                {step > 1 && (
                                    <button
                                        type="button"
                                        onClick={previous}
                                        className="px-4 py-2 bg-gray-200 rounded"
                                    >
                                        Sebelumnya
                                    </button>
                                )}

                                {step < 4 ? (
                                    <button
                                        type="button"
                                        onClick={() => next(validateForm, setTouched)}
                                        className="px-4 py-2 bg-blue-500 text-white rounded"
                                    >
                                        Selanjutnya
                                    </button>
                                ) : (

                                    // <button
                                    //     type="submit"
                                    //     disabled={!values.agreement || isSubmitting}
                                    //     className={`px-4 py-2 rounded ${values.agreement
                                    //         ? "bg-green-500 text-white"
                                    //         : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    //         }`}
                                    // >
                                    //     Simpan
                                    // </button>
                                    <button
                                        type="submit"
                                        disabled={!values.agreement || isSubmitting}
                                        className={`
    px-5 py-2 rounded-lg font-medium transition
    ${values.agreement
                                                ? "bg-green-600 hover:bg-green-700 text-white"
                                                : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                            }
  `}
                                    >
                                        {isSubmitting ? "Mengirim..." : "Kirim Permohonan"}
                                    </button>


                                )}
                            </div>
                        </Form>
                    }

                    }
                </Formik>
            </div>
        </div>
    );
}
